/**
 * CORE-1342 — `FeedItem.refId`: одна строка, два вида смысла.
 *
 * Сервер отдаёт ссылку с префиксом вида: `live:42`, `post:1258`, `room:7`,
 * `cm:<guid>`. Строкой это сделано намеренно — в контракте прямо сказано,
 * что так помещаются и целочисленные id (Live, WatchParty, практики), и
 * Guid (круги, их участники и события).
 *
 * Клиент до сих пор объявлял поле числом. Последствия были настоящие:
 * навигация уходила со строкой «live:42» вместо 42, `openFeedItem` получал
 * NaN и молча не открывал ничего, а в `SavedEventsContext` в одной карте
 * оказывались числовые и строковые ключи, из-за чего закладка не находилась.
 *
 * Отсюда правило: **ссылка живёт строкой**, а число из неё достаётся явно и
 * только там, где оно действительно нужно, — и там, где его может не быть,
 * это видно по типу.
 */

/** Собрать ссылку в том же виде, в каком её отдаёт сервер. */
export function makeFeedRefId(prefix: string, id: number | string): string {
  return `${prefix}:${id}`;
}

/**
 * Числовой id из ссылки. `null` — когда его там нет: это либо Guid-вид
 * (круги), либо мусор. Возвращать 0 или NaN нельзя: ноль уводит в
 * несуществующую запись, а NaN расползается дальше молча.
 */
export function feedNumericId(refId: string | number | null | undefined): number | null {
  if (typeof refId === 'number') return Number.isFinite(refId) && refId > 0 ? refId : null;
  if (typeof refId !== 'string' || refId === '') return null;
  const tail = refId.includes(':') ? refId.slice(refId.lastIndexOf(':') + 1) : refId;
  if (!/^\d+$/.test(tail)) return null;
  const n = Number(tail);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Хвост ссылки как есть — для Guid-видов, где число не нужно. */
export function feedRawId(refId: string | number | null | undefined): string {
  if (refId == null) return '';
  const s = String(refId);
  return s.includes(':') ? s.slice(s.lastIndexOf(':') + 1) : s;
}

/**
 * Префикс ссылки по виду карточки — ровно тот, что ставит сервер
 * (`FeedService`). Нужен там, где карточку собирает сам клиент: локальный
 * элемент и серверный попадают в одни и те же списки и карты, и разойтись по
 * форме ссылки они не должны.
 *
 * Чего здесь нет намеренно: `cm` и `ce` (круги) — их id Guid, и собирать их
 * на клиенте неоткуда; `us`/`ms` (подписки) — эти карточки строит только
 * сервер.
 */
const FEED_REF_PREFIX: Record<string, string> = {
  live_scheduled: 'live',
  live_recorded: 'live',
  room_scheduled: 'room',
  room_recorded: 'room',
  practice_uploaded: 'practice',
  master_post_published: 'post',
  user_post_published: 'post',
  master_event_created: 'me',
  event_saved: 'se',
  rsvp: 'se',
  // Офлайн-события приезжают тем же видом, что и события мастера.
  offline_scheduled: 'me',
  offline_recorded: 'me',
};

/** Ссылка для карточки, собранной на клиенте. */
export function feedRefFor(kind: string, id: number | string): string {
  const prefix = FEED_REF_PREFIX[kind];
  return prefix ? makeFeedRefId(prefix, id) : String(id);
}
